import { useCallback, useEffect, useRef, useState } from "react";
import type {
  CastMember,
  Comment,
  Cue,
  EpisodeView,
  Id,
  ScriptColumn,
  ScriptDocument,
} from "@supermanus/document-model";
import {
  formatDuration,
  formatEpisodeDisplayTitle,
  getCueNote,
  getSceneNote,
  getEpisodeScenes,
  getVisibleNoteColumns,
  getSceneCues,
  listEpisodes,
  aggregateEpisodeCredits,
  getEpisodeProgramTexts,
  totalSceneDuration,
  layoutDualColumnSpans,
  episodeStatusProgress,
} from "@supermanus/document-model";
import { DocumentHead, VIEW_LABEL, formatDocumentEpisodeLine } from "./DocumentHead";
import { PaperFit } from "./PaperFit";
import { EpisodeStatusStats } from "./StatusProgressBar";
import {
  clearPagedInsertedStyles,
  paginatePrintHtml,
  pdfFromPagedRoot,
  suggestedPdfFilename,
} from "./printPdf";
import { pickAndWritePdfFile } from "./exportFile";
import { sanitizeCueHtml, isCueContentEmpty } from "./cueRichText";
import { SHOW_REPLIK_UI } from "./replikFeature";
import { PrintPageChrome } from "./PrintPageChrome";
import { NoteRichText } from "./SourcesView";
import { cueShowsRowRule, isAudioOnlyColumnOrder, type RowRuleMode, type ScriptColumnOrder } from "./editorSettings";
import { isEmptyForAudioOnlyPrint } from "./manuscriptExport";
import { StatusPrintScenes } from "./StatusView";

function layoutRows(
  cues: Cue[],
  castMembers: CastMember[],
  comments: Comment[],
  rowRuleMode: RowRuleMode,
  audioOnly = false,
) {
  const rows: {
    key: string;
    cueId: Id;
    setupIndex: number;
    visual: string | null;
    visualSpan: number;
    audio: string | null;
    audioSpan: number;
    characterName?: string;
    visualCharacterName?: string;
    dialogueColor?: string;
    visualColor?: string;
    printBorderBelow: boolean;
    visualRuleBelow: boolean;
    cameraNumber?: number;
    cameraNote?: string;
    positionNote?: string;
    visualComments: Comment[];
    audioComments: Comment[];
  }[] = [];

  function cellComments(cueId: Id, column: ScriptColumn) {
    return comments.filter(
      (c) => c.cueId === cueId && c.column === column && !c.resolved,
    );
  }

  function sideMeta(cue: Cue, column: ScriptColumn) {
    const name = (column === "audio" ? cue.audio : cue.visual).characterName?.trim();
    if (!SHOW_REPLIK_UI || !name) return { characterName: undefined, color: undefined };
    const cast = castMembers.find(
      (m) => m.name.trim().toLowerCase() === name.toLowerCase(),
    );
    const showName = cast ? cast.showNameAboveLines : true;
    return {
      characterName: showName ? name : undefined,
      color: cast?.color?.trim() || undefined,
    };
  }

  if (audioOnly) {
    for (let i = 0; i < cues.length; i++) {
      const cue = cues[i]!;
      if (isEmptyForAudioOnlyPrint(cue)) continue;
      const audio = sideMeta(cue, "audio");
      rows.push({
        key: cue.id,
        cueId: cue.id,
        setupIndex: i + 1,
        visual: null,
        visualSpan: 0,
        audio: cue.audio.content,
        audioSpan: 1,
        characterName: audio.characterName,
        dialogueColor: audio.color,
        printBorderBelow: cueShowsRowRule(cue, rowRuleMode),
        visualRuleBelow: false,
        cameraNumber: cue.cameraNumber,
        cameraNote: cue.cameraNote,
        positionNote: cue.positionNote,
        visualComments: [],
        audioComments: cellComments(cue.id, "audio"),
      });
    }
    return rows;
  }

  const spans = layoutDualColumnSpans(cues);

  for (let i = 0; i < cues.length; i++) {
    const cue = cues[i]!;
    const visualSpan = spans[i]?.visualSpan ?? 1;
    const audioSpan = spans[i]?.audioSpan ?? 1;

    let visualRuleBelow = false;
    if (visualSpan > 1) {
      const endCue = cues[i + visualSpan - 1];
      visualRuleBelow = cueShowsRowRule(endCue, rowRuleMode);
    } else if (visualSpan === 1) {
      visualRuleBelow = cueShowsRowRule(cue, rowRuleMode);
    }

    const audio = audioSpan > 0 ? sideMeta(cue, "audio") : undefined;
    const visual = visualSpan > 0 ? sideMeta(cue, "visual") : undefined;

    rows.push({
      key: cue.id,
      cueId: cue.id,
      setupIndex: i + 1,
      visual: visualSpan > 0 ? cue.visual.content : null,
      visualSpan,
      audio: audioSpan > 0 ? cue.audio.content : null,
      audioSpan,
      characterName: audio?.characterName,
      visualCharacterName: visual?.characterName,
      dialogueColor: audio?.color,
      visualColor: visual?.color,
      printBorderBelow: cueShowsRowRule(cue, rowRuleMode),
      visualRuleBelow,
      cameraNumber: cue.cameraNumber,
      cameraNote: cue.cameraNote,
      positionNote: cue.positionNote,
      visualComments:
        visualSpan > 0 ? cellComments(cue.id, "visual") : [],
      audioComments: audioSpan > 0 ? cellComments(cue.id, "audio") : [],
    });
  }
  return rows;
}

function Paras({ text }: { text: string }) {
  return (
    <div
      className="sm-rich-paras"
      dangerouslySetInnerHTML={{ __html: sanitizeCueHtml(text) }}
    />
  );
}

function printBreakBeforeClass(on: boolean, index: number): string {
  return on && index > 0 ? " sm-print-break-before" : "";
}

function sceneRunningLabel(scene: {
  number: string | number;
  heading?: string | null;
  title?: string | null;
}): string {
  return [
    `Scene ${scene.number}`,
    scene.heading?.trim() || null,
    scene.title?.trim() || null,
  ]
    .filter(Boolean)
    .join(" · ");
}

type ScriptPrintRange = "current" | "selected" | "episode";

function scenesForScriptPrint<T extends { id: Id }>(
  scenes: T[],
  range: ScriptPrintRange,
  activeSceneId: Id | null | undefined,
  pickedIds: Id[],
): T[] {
  if (range === "current") {
    const current =
      scenes.find((s) => s.id === activeSceneId) ?? scenes[0];
    return current ? [current] : [];
  }
  if (range === "selected") {
    const picked = new Set(pickedIds);
    return scenes.filter((s) => picked.has(s.id));
  }
  return scenes;
}

function scenePickLabel(scene: {
  number: string | number;
  title?: string | null;
  heading?: string | null;
}): string {
  const title = scene.title?.trim() || scene.heading?.trim() || "Unavngiven";
  return `${scene.number}. ${title}`;
}

function CommentBlock({ comments }: { comments: Comment[] }) {
  if (comments.length === 0) return null;
  return (
    <div className="sm-print-comments">
      {comments.map((c) => (
        <div key={c.id} className="sm-print-comment">
          <strong>{c.authorName}</strong>
          {c.quote ? <em>«{c.quote}»</em> : null}
          <span>{c.body}</span>
        </div>
      ))}
    </div>
  );
}

export interface PrintPreviewProps {
  document: ScriptDocument;
  episodeId: Id | null;
  view?: EpisodeView;
  showColumnRule?: boolean;
  rowRuleMode?: RowRuleMode;
  defaultSceneHeaderBg?: string;
  showHours?: boolean;
  showFrames?: boolean;
  fps?: number;
  showSetupNumbers?: boolean;
  showCameraNotes?: boolean;
  showPosition?: boolean;
  columnOrder?: ScriptColumnOrder;
  activeNoteColumnId?: Id | null;
  /** Scene currently open in manus — used for “aktuel scene”. */
  activeSceneId?: Id | null;
  onClose: () => void;
  onExportRtf?: (sceneIds: Id[]) => void;
  onExportDocx?: (sceneIds: Id[]) => void;
  onExportAudio?: (sceneIds: Id[]) => void;
  onExportAudioDocx?: (sceneIds: Id[]) => void;
  lastSavedAt?: Date | null;
  exportError?: string | null;
  showEpisodeNumbers?: boolean;
}

export function PrintPreview({
  document,
  episodeId,
  view = "script",
  showColumnRule = true,
  rowRuleMode = "custom",
  defaultSceneHeaderBg = "#dce8e2",
  showHours = true,
  showFrames = false,
  fps = 25,
  showSetupNumbers = false,
  showCameraNotes = false,
  showPosition = false,
  columnOrder = "visual-audio",
  activeNoteColumnId = null,
  activeSceneId = null,
  onClose,
  onExportRtf,
  onExportDocx,
  onExportAudio,
  onExportAudioDocx,
  lastSavedAt,
  exportError,
  showEpisodeNumbers = false,
}: PrintPreviewProps) {
  const [withComments, setWithComments] = useState(false);
  const [pageBreakBeforeScene, setPageBreakBeforeScene] = useState(false);
  const [repeatSceneHead, setRepeatSceneHead] = useState(false);
  const [showCrewSceneNumbers, setShowCrewSceneNumbers] = useState(false);
  const [scriptPrintRange, setScriptPrintRange] =
    useState<ScriptPrintRange>("current");
  const [statusPrintExpand, setStatusPrintExpand] = useState<
    "collapsed" | "expanded"
  >("collapsed");
  const [statusPrintCompact, setStatusPrintCompact] = useState(false);
  const handleStatusCompactLabels = useCallback((compact: boolean) => {
    setStatusPrintCompact(compact);
  }, []);
  const [pickedSceneIds, setPickedSceneIds] = useState<Id[]>(() =>
    activeSceneId ? [activeSceneId] : [],
  );
  const [printedAt] = useState(() => new Date());
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfMsg, setPdfMsg] = useState("Opdeler i A4-sider…");
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfProgress, setPdfProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [pageMode, setPageMode] = useState<"paging" | "paged" | "fallback">(
    "paging",
  );
  const [exportOpen, setExportOpen] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const pdfBlobRef = useRef<Blob | null>(null);
  const sourceRef = useRef<HTMLDivElement>(null);
  const pagedRef = useRef<HTMLDivElement>(null);
  const episodes = listEpisodes(document);
  const episode =
    (episodeId && document.episodes[episodeId]) || episodes[0] || null;
  const season =
    episode?.seasonId && document.seasons[episode.seasonId]
      ? document.seasons[episode.seasonId]
      : null;
  const scenes = episode ? getEpisodeScenes(document, episode.id) : [];
  const allComments = Object.values(document.comments ?? {}).filter(
    (c) => !c.resolved,
  );
  const durFmt = { showHours, showFrames, fps };
  const total = totalSceneDuration(scenes);
  const isScript = view === "script";
  const isNotes = view === "sources";
  const usesScenePrintRange = isScript || isNotes;
  const printScenes = usesScenePrintRange
    ? scenesForScriptPrint(
        scenes,
        scriptPrintRange,
        activeSceneId,
        pickedSceneIds,
      )
    : scenes;
  const printSceneIds = printScenes.map((s) => s.id);
  const printSceneKey = printScenes.map((s) => s.id).join(",");
  const landscape = (isScript && withComments) || view === "sources";
  const noteColumns = getVisibleNoteColumns(document, episode?.id ?? null);
  const primaryNoteColumnId = noteColumns[0]?.id ?? null;
  const printNoteColumnId =
    (activeNoteColumnId &&
    noteColumns.some((c) => c.id === activeNoteColumnId)
      ? activeNoteColumnId
      : primaryNoteColumnId) ?? null;
  const printNoteTitle =
    noteColumns.find((c) => c.id === printNoteColumnId)?.title.trim() ||
    "Noter";

  function handlePrint() {
    const style = window.document.createElement("style");
    style.setAttribute("data-sm-print-page", "1");
    /* Margin 0: print chrome + paper padding provide header/footer bands. */
    style.textContent = landscape
      ? "@page { size: A4 landscape; margin: 0; } * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }"
      : "@page { size: A4 portrait; margin: 0; } * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }";
    window.document.head.appendChild(style);
    const cleanup = () => style.remove();
    window.addEventListener("afterprint", cleanup, { once: true });
    window.print();
    window.setTimeout(cleanup, 60_000);
  }

  useEffect(() => {
    if (!exportOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!exportMenuRef.current?.contains(e.target as Node)) {
        setExportOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setExportOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [exportOpen]);

  async function ensurePdfBlob(
    onProgress?: (done: number, total: number) => void,
  ): Promise<Blob> {
    if (pdfBlobRef.current) return pdfBlobRef.current;
    const dest = pagedRef.current;
    if (pageMode !== "paged" || !dest?.querySelector(".pagedjs_page")) {
      throw new Error("Ingen A4-sider");
    }
    const blob = await pdfFromPagedRoot(dest, landscape, onProgress);
    pdfBlobRef.current = blob;
    return blob;
  }

  useEffect(() => {
    pdfBlobRef.current = null;
    if (!episode) return;
    let cancelled = false;
    setPageMode("paging");
    setPdfBusy(false);
    setPdfProgress(null);
    setPdfError(null);
    setPdfMsg("Opdeler i A4-sider…");
    const timer = window.setTimeout(() => {
      void (async () => {
        const src = sourceRef.current;
        const dest = pagedRef.current;
        if (!src || !dest) {
          if (!cancelled) {
            setPageMode("fallback");
            setPdfError(
              "A4-sider kunne ikke opbygges. Brug Udskriv…, eller prøv igen.",
            );
          }
          return;
        }
        try {
          const article = src.querySelector("article") ?? src;
          await paginatePrintHtml(article.innerHTML, dest, landscape, {
            repeatSceneHead,
          });
          if (cancelled) return;
          setPdfMsg("");
          setPdfError(null);
          setPageMode("paged");
        } catch {
          if (!cancelled) {
            setPageMode("fallback");
            setPdfMsg("");
            setPdfError(
              "A4-sider kunne ikke opbygges. Brug Udskriv…, eller prøv igen.",
            );
          }
        }
      })();
    }, 50);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      clearPagedInsertedStyles();
    };
  }, [
    episode,
    landscape,
    view,
    withComments,
    pageBreakBeforeScene,
    repeatSceneHead,
    showCrewSceneNumbers,
    statusPrintExpand,
    statusPrintCompact,
    document.production.updatedAt,
    printSceneKey,
  ]);

  async function handleSavePdf() {
    if (!episode) return;
    // Never fall back to window.print() — that opens the system print dialog
    // where "Save as PDF" is easy to miss. Gem som PDF must download a file.
    if (pageMode !== "paged") {
      setPdfError(
        "PDF kan først gemmes, når A4-siderne er klar. Vent, eller brug Udskriv…",
      );
      return;
    }
    const filename = suggestedPdfFilename(document, episode.id);
    async function savePdfBlob(blob: Blob) {
      await pickAndWritePdfFile(filename, blob);
      setPdfError(null);
    }
    if (pdfBlobRef.current) {
      await savePdfBlob(pdfBlobRef.current);
      return;
    }
    setPdfBusy(true);
    setPdfProgress(null);
    setPdfError(null);
    setPdfMsg("Bygger PDF…");
    try {
      const blob = await ensurePdfBlob((done, total) => {
        setPdfProgress({ done, total });
        setPdfMsg(`Bygger PDF… side ${done} af ${total}`);
      });
      setPdfMsg("Vælg filnavn…");
      await savePdfBlob(blob);
      setPdfMsg("");
      setPdfProgress(null);
      setPdfError(null);
    } catch (err) {
      const detail =
        err instanceof Error && err.message.trim()
          ? err.message.trim()
          : "Ukendt fejl";
      const message = `PDF kunne ikke bygges (${detail}). Prøv igen, eller brug Udskriv…`;
      setPdfMsg("");
      setPdfError(message);
      window.alert(message);
    } finally {
      setPdfBusy(false);
      setPdfProgress(null);
    }
  }

  if (!episode) {
    return (
      <div className="sm-print-overlay">
        <div className="sm-print-toolbar sm-no-print">
          <button type="button" onClick={onClose}>
            Luk
          </button>
        </div>
        <p>Ingen episode at vise.</p>
      </div>
    );
  }

  const hint =
    view === "timing"
      ? "A4 stående · sceneliste"
      : view === "outline"
        ? "A4 stående · runddown"
        : view === "crew"
          ? "A4 stående · creditliste"
          : view === "programtext"
            ? "A4 stående · programtekst"
            : view === "sources"
            ? "A4 liggende · noter"
            : view === "status" || view === "home"
              ? "A4 stående · status"
              : withComments
                ? "A4 liggende · kommentarspalte"
                : "A4 stående · to kolonner";

  const runningProdTitle = document.production.title.trim();
  const episodeDisplayTitle = episode
    ? formatEpisodeDisplayTitle(episode, showEpisodeNumbers)
    : "";
  const runningEpisodeLine = formatDocumentEpisodeLine(
    season?.title,
    episodeDisplayTitle,
    view === "home" ? "status" : view,
  );
  const statusRollup = episodeStatusProgress(document, episode.id);
  const rtfTitle = exportError
    ? exportError
    : lastSavedAt
      ? `Sidst gemt ${lastSavedAt.toLocaleTimeString("da-DK", {
          hour: "2-digit",
          minute: "2-digit",
        })}`
      : "Gem episoden som RTF med samme A4-layout som udskrift";
  const pdfDisabled =
    pageMode !== "paged" || pdfBusy || printScenes.length === 0;
  const pdfButtonTitle =
    pageMode === "paging"
      ? "Vent, til A4-siderne er klar"
      : pageMode === "fallback"
        ? "PDF kræver A4-sider — brug Udskriv… i stedet"
        : printScenes.length === 0
          ? "Vælg mindst én scene"
          : pdfBusy
            ? "Bygger PDF…"
            : "Download PDF-fil (ikke browserens udskriftsdialog)";
  const showBuildOverlay = pageMode === "paging" || pdfBusy;
  const buildOverlayTitle = pdfBusy
    ? "Bygger PDF"
    : "Opdeler i A4-sider";
  const buildOverlayDetail = pdfBusy
    ? pdfProgress && pdfProgress.total > 0
      ? `Side ${pdfProgress.done} af ${pdfProgress.total}`
      : "Klargør sider…"
    : "Manuset lægges ud på A4-sider. Vent et øjeblik.";
  const buildOverlayPct =
    pdfBusy && pdfProgress && pdfProgress.total > 0
      ? Math.min(
          100,
          Math.round((pdfProgress.done / pdfProgress.total) * 100),
        )
      : null;

  return (
    <div
      className={`sm-print-overlay${landscape ? " sm-print-with-comments" : ""}${
        pageMode === "paged" ? " is-paged" : ""
      }${showBuildOverlay ? " is-building" : ""}`}
    >
      <div className="sm-print-toolbar sm-no-print">
        <div className="sm-print-toolbar-main">
          <strong>Forbered udskrift · {VIEW_LABEL[view]}</strong>
          <span
            className={`sm-print-toolbar-hint${
              pdfError ? " is-error" : ""
            }`}
          >
            {pdfError
              ? pdfError
              : pageMode === "paging"
                ? pdfMsg
                : isScript && printScenes.length === 0
                  ? "Vælg mindst én scene"
                  : pdfMsg ||
                    (pageMode === "paged"
                      ? "A4-sider"
                      : hint)}
          </span>
          {usesScenePrintRange && scenes.length > 0 ? (
            <label className="sm-print-option">
              <span>Udskriv</span>
              <select
                aria-label="Vælg scener til udskrift"
                value={scriptPrintRange}
                onChange={(e) => {
                  const next = e.target.value as ScriptPrintRange;
                  setScriptPrintRange(next);
                  if (next === "selected" && pickedSceneIds.length === 0) {
                    const fallback = activeSceneId ?? scenes[0]?.id;
                    if (fallback) setPickedSceneIds([fallback]);
                  }
                }}
              >
                <option value="current">Aktuel scene</option>
                <option value="selected">Udvalgte scener</option>
                <option value="episode">Hele episoden</option>
              </select>
            </label>
          ) : null}
          {usesScenePrintRange &&
          scriptPrintRange === "selected" &&
          scenes.length > 0 ? (
            <details className="sm-print-scene-pick">
              <summary>
                {printScenes.length === 0
                  ? "Vælg scener"
                  : `${printScenes.length} ${
                      printScenes.length === 1 ? "scene" : "scener"
                    }`}
              </summary>
              <div className="sm-print-scene-pick-list" role="group">
                {scenes.map((s) => {
                  const checked = pickedSceneIds.includes(s.id);
                  return (
                    <label key={s.id} className="sm-print-scene-pick-item">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          setPickedSceneIds((prev) =>
                            checked
                              ? prev.filter((id) => id !== s.id)
                              : [...prev, s.id],
                          );
                        }}
                      />
                      <span>{scenePickLabel(s)}</span>
                    </label>
                  );
                })}
              </div>
            </details>
          ) : null}
          <div className="sm-page-more" ref={exportMenuRef}>
            <button
              type="button"
              aria-expanded={exportOpen}
              aria-haspopup="true"
              className={exportOpen ? "is-active" : undefined}
              onClick={() => setExportOpen((v) => !v)}
            >
              Eksportér som …
            </button>
            {exportOpen ? (
              <div className="sm-page-more-pop" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  disabled={pdfDisabled}
                  title={pdfButtonTitle}
                  onClick={() => {
                    setExportOpen(false);
                    void handleSavePdf();
                  }}
                >
                  {pdfBusy ? pdfMsg || "Gemmer PDF…" : "Gem som PDF"}
                </button>
                {onExportRtf ? (
                  <button
                    type="button"
                    role="menuitem"
                    title={rtfTitle}
                    onClick={() => {
                      setExportOpen(false);
                      onExportRtf(printSceneIds);
                    }}
                  >
                    Gem RTF
                  </button>
                ) : null}
                {onExportDocx ? (
                  <button
                    type="button"
                    role="menuitem"
                    title="Gem episoden som Word-dokument (.docx) — samme layout som udskrift"
                    onClick={() => {
                      setExportOpen(false);
                      onExportDocx(printSceneIds);
                    }}
                  >
                    Gem Word
                  </button>
                ) : null}
                {onExportAudio ? (
                  <button
                    type="button"
                    role="menuitem"
                    title="Gem kun lydsiden som RTF-tekst"
                    onClick={() => {
                      setExportOpen(false);
                      onExportAudio(printSceneIds);
                    }}
                  >
                    Gem lydside (RTF)
                  </button>
                ) : null}
                {onExportAudioDocx ? (
                  <button
                    type="button"
                    role="menuitem"
                    title="Gem kun lydsiden som Word-dokument"
                    onClick={() => {
                      setExportOpen(false);
                      onExportAudioDocx(printSceneIds);
                    }}
                  >
                    Gem lydside (Word)
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={handlePrint}
            disabled={pageMode === "paging" || printScenes.length === 0}
          >
            Udskriv…
          </button>
          <button type="button" onClick={onClose} disabled={pdfBusy}>
            Luk
          </button>
        </div>
        <div className="sm-print-toolbar-options">
          {view !== "crew" &&
          view !== "programtext" &&
          printScenes.length > 1 ? (
            <label className="sm-print-option">
              <input
                type="checkbox"
                checked={pageBreakBeforeScene}
                onChange={(e) => setPageBreakBeforeScene(e.target.checked)}
              />
              <span>Sideskift før ny scene</span>
            </label>
          ) : null}
          {printScenes.length > 0 &&
          (isScript || view === "sources" || view === "outline") ? (
            <label className="sm-print-option">
              <input
                type="checkbox"
                checked={repeatSceneHead}
                onChange={(e) => setRepeatSceneHead(e.target.checked)}
              />
              <span>Gentag sceneoverskrift på nye sider</span>
            </label>
          ) : null}
          {view === "status" || view === "home" ? (
            <label className="sm-print-option">
              <span>Vis</span>
              <select
                aria-label="Vælg statusvisning til udskrift"
                value={statusPrintExpand}
                onChange={(e) =>
                  setStatusPrintExpand(
                    e.target.value === "expanded" ? "expanded" : "collapsed",
                  )
                }
              >
                <option value="collapsed">Sammenklappet</option>
                <option value="expanded">Udvidet med trin</option>
              </select>
            </label>
          ) : null}
          {view === "crew" ? (
            <label className="sm-print-option">
              <input
                type="checkbox"
                checked={showCrewSceneNumbers}
                onChange={(e) => setShowCrewSceneNumbers(e.target.checked)}
              />
              <span>Vis hvilke scener hver person er krediteret i</span>
            </label>
          ) : null}
          {isScript ? (
            <label className="sm-print-option">
              <input
                type="checkbox"
                checked={withComments}
                onChange={(e) => setWithComments(e.target.checked)}
              />
              <span>Udskriv med kommentarer</span>
            </label>
          ) : null}
        </div>
      </div>

      <div className="sm-print-stage">
      {showBuildOverlay ? (
        <div
          className="sm-print-build-overlay sm-no-print"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="sm-print-build-card">
            <p className="sm-print-build-kicker">
              {pdfBusy ? "Eksport" : "Forberedelse"}
            </p>
            <h2 className="sm-print-build-title">{buildOverlayTitle}</h2>
            <p className="sm-print-build-detail">{buildOverlayDetail}</p>
            <div
              className={`sm-print-build-track${
                buildOverlayPct == null ? " is-indeterminate" : ""
              }`}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={buildOverlayPct ?? undefined}
              aria-label={buildOverlayTitle}
            >
              <div
                className="sm-print-build-fill"
                style={
                  buildOverlayPct != null
                    ? { width: `${buildOverlayPct}%` }
                    : undefined
                }
              />
            </div>
            {buildOverlayPct != null ? (
              <p className="sm-print-build-pct">{buildOverlayPct}%</p>
            ) : (
              <p className="sm-print-build-pct sm-print-build-pct--pulse">
                Arbejder…
              </p>
            )}
            <p className="sm-print-build-hint">
              {pdfBusy
                ? "Luk ikke fanen, før eksporten er færdig."
                : "Det kan tage et øjeblik ved lange episoder."}
            </p>
          </div>
        </div>
      ) : null}
      <div
        ref={sourceRef}
        className={
          pageMode === "fallback" ? "sm-print-pages" : "sm-print-source"
        }
      >
        <PaperFit widthMm={landscape ? 297 : 210}>
        <article
          className={`sm-paper sm-print-sheet${
            landscape ? " sm-paper-a4-landscape" : " sm-paper-a4"
          }${pageBreakBeforeScene ? " is-scene-page-break" : ""}`}
        >
          {pageMode === "fallback" ? (
          <PrintPageChrome
            landscape={landscape}
            productionTitle={document.production.title}
            seasonTitle={season?.title}
            episodeTitle={episodeDisplayTitle}
            view={view === "home" ? "status" : view}
            printedAt={printedAt}
          />
          ) : (
            <div className="sm-print-running-head">
              {runningProdTitle ? (
                <strong>{document.production.title}</strong>
              ) : null}
              {runningEpisodeLine ? (
                <span>{runningEpisodeLine}</span>
              ) : null}
            </div>
          )}
          {pageMode === "fallback" ? (
          <div className="sm-no-print">
            <DocumentHead
              productionTitle={document.production.title}
              seasonTitle={season?.title}
              episodeTitle={episodeDisplayTitle}
              view={view === "home" ? "status" : view}
              printedAt={printedAt}
            />
          </div>
          ) : null}

          {view === "timing" ? (
            <table className="sm-print-timing-table">
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Titel</th>
                  <th scope="col" className="sm-print-timing-dur">
                    Varighed
                  </th>
                </tr>
              </thead>
              <tbody>
                {scenes.map((scene, sceneIndex) => {
                  const headerBg =
                    scene.headerBackground?.trim() || defaultSceneHeaderBg;
                  return (
                    <tr
                      key={scene.id}
                      className={printBreakBeforeClass(
                        pageBreakBeforeScene,
                        sceneIndex,
                      ).trim()}
                      style={{ backgroundColor: headerBg }}
                    >
                      <td>{scene.number}</td>
                      <td>
                        {scene.title}
                        {scene.heading?.trim() ? (
                          <span className="sm-print-timing-heading">
                            {scene.heading}
                          </span>
                        ) : null}
                      </td>
                      <td className="sm-print-timing-dur">
                        {formatDuration(scene.durationSeconds, durFmt)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}>I alt</td>
                  <td className="sm-print-timing-dur">
                    {formatDuration(total, durFmt)}
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : null}

          {view === "outline" ? (
            <div className="sm-print-outline">
              {scenes.map((scene, sceneIndex) => {
                const headerBg =
                  scene.headerBackground?.trim() || defaultSceneHeaderBg;
                return (
                  <section
                    key={scene.id}
                    className={`sm-print-outline-scene${printBreakBeforeClass(pageBreakBeforeScene, sceneIndex)}`}
                    style={{ backgroundColor: headerBg }}
                  >
                    <div
                      className="sm-print-outline-top"
                      data-scene-running={sceneRunningLabel(scene)}
                    >
                      <span className="sm-print-scene-num-line">
                        Scene {scene.number}
                        {scene.heading?.trim() ? (
                          <span className="sm-print-scene-heading">
                            {" "}
                            {scene.heading}
                          </span>
                        ) : null}
                      </span>
                      <span className="sm-print-dur">
                        {formatDuration(scene.durationSeconds, durFmt)}
                      </span>
                    </div>
                    <h3 className="sm-print-outline-title">{scene.title}</h3>
                    {scene.description.trim() ? (
                      <p className="sm-print-outline-desc">
                        {scene.description}
                      </p>
                    ) : null}
                  </section>
                );
              })}
              <div className="sm-print-outline-total">
                <span>I alt</span>
                <span>{formatDuration(total, durFmt)}</span>
              </div>
            </div>
          ) : null}

          {view === "crew" ? (
            <div className="sm-print-crew">
              {(() => {
                const credits = aggregateEpisodeCredits(document, episode.id);
                if (credits.length === 0) {
                  return <p className="sm-print-empty">Ingen credits endnu.</p>;
                }
                return (
                  <div className="sm-print-crew-blocks">
                    {credits.map((block) => (
                      <div key={block.jobTitle} className="sm-print-crew-block">
                        <h3>{block.jobTitle}</h3>
                        <ul>
                          {block.people.map((p) => (
                            <li key={`${p.name}|${p.detail ?? ""}`}>
                              {p.name}
                              {p.detail ? ` — ${p.detail}` : ""}
                              {showCrewSceneNumbers &&
                              (p.episodeWide || p.sceneNumbers.length > 0) ? (
                                <span className="sm-print-crew-scenes">
                                  {" "}
                                  (
                                  {[
                                    p.episodeWide ? "hele episoden" : null,
                                    p.sceneNumbers.length
                                      ? `sc. ${p.sceneNumbers.join(", ")}`
                                      : null,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")}
                                  )
                                </span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          ) : null}

          {view === "programtext" ? (
            <div className="sm-print-programtext">
              {(() => {
                const texts = getEpisodeProgramTexts(episode);
                if (texts.length === 0) {
                  return (
                    <p className="sm-print-empty">Ingen programtekster endnu.</p>
                  );
                }
                return texts.map((text) => (
                  <section
                    key={text.id}
                    className="sm-print-programtext-block"
                  >
                    <h3>{text.title.trim() || "Uden navn"}</h3>
                    {text.body.trim() ? (
                      <p className="sm-print-programtext-body">{text.body}</p>
                    ) : (
                      <p className="sm-print-empty">Tom.</p>
                    )}
                  </section>
                ));
              })()}
            </div>
          ) : null}

          {view === "sources"
            ? printScenes.map((scene, sceneIndex) => {
                const cues = getSceneCues(document, scene.id);
                const headerBg =
                  scene.headerBackground?.trim() || defaultSceneHeaderBg;
                return (
                  <section
                    key={scene.id}
                    className={`sm-print-scene sm-print-sources${printBreakBeforeClass(pageBreakBeforeScene, sceneIndex)}`}
                  >
                    <h2
                      className="sm-print-scene-head"
                      data-scene-running={sceneRunningLabel(scene)}
                      style={{ backgroundColor: headerBg }}
                    >
                      <span className="sm-print-scene-num-line">
                        Scene {scene.number}
                        {scene.heading?.trim() ? (
                          <span className="sm-print-scene-heading">
                            {" "}
                            {scene.heading}
                          </span>
                        ) : null}
                      </span>
                      <span className="sm-print-scene-title">
                        {scene.title}
                      </span>
                      <span className="sm-print-dur">
                        {formatDuration(scene.durationSeconds, durFmt)}
                      </span>
                    </h2>
                    {(() => {
                      const sceneNote = getSceneNote(
                        scene,
                        printNoteColumnId,
                      ).trim();
                      if (!sceneNote) return null;
                      return (
                        <div className="sm-print-outline-desc sm-print-scene-note">
                          <NoteRichText text={sceneNote} />
                        </div>
                      );
                    })()}
                    <table
                      className={`sm-print-table sm-print-table-sources${
                        showColumnRule && !isAudioOnlyColumnOrder(columnOrder)
                          ? " sm-col-rule"
                          : ""
                      }`}
                    >
                      <thead>
                        <tr>
                          {isAudioOnlyColumnOrder(columnOrder) ? (
                            <th scope="col">Lydside</th>
                          ) : columnOrder === "audio-visual" ? (
                            <>
                              <th scope="col">Lydside</th>
                              <th scope="col">Billedside</th>
                            </>
                          ) : (
                            <>
                              <th scope="col">Billedside</th>
                              <th scope="col">Lydside</th>
                            </>
                          )}
                          <th scope="col">{printNoteTitle}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cues.map((cue, cueIndex) => {
                          if (
                            isAudioOnlyColumnOrder(columnOrder) &&
                            isEmptyForAudioOnlyPrint(cue)
                          ) {
                            return null;
                          }
                          const kam =
                            cue.cameraNumber && cue.cameraNumber >= 1
                              ? cue.cameraNumber
                              : undefined;
                          const kamNote = cue.cameraNote?.trim() ?? "";
                          const posNote = cue.positionNote?.trim() ?? "";
                          const showCol1 = showSetupNumbers || showCameraNotes;
                          const showMeta = showCol1 || showPosition;
                          const cameraPrint =
                            showCameraNotes && (kam != null || kamNote) ? (
                              <span className="sm-print-camera-note">
                                {kam != null ? `KAM ${kam}` : null}
                                {kamNote ? (
                                  <span className="sm-print-camera-text">
                                    {kam != null ? " " : null}
                                    {kamNote}
                                  </span>
                                ) : null}
                              </span>
                            ) : null;
                          const col1Meta = showMeta ? (
                            <div
                              className={
                                showCol1
                                  ? "sm-print-meta-row"
                                  : "sm-print-meta-row sm-cue-meta-row-spacer"
                              }
                              aria-hidden={!showCol1}
                            >
                              {showSetupNumbers ? (
                                <span className="sm-print-setup-num">
                                  {scene.number}-{cueIndex + 1}
                                </span>
                              ) : null}
                              {cameraPrint}
                              {!showCol1 && showPosition ? (
                                <span className="sm-print-position-note">
                                  Pos.
                                  {posNote ? (
                                    <span className="sm-print-position-text">
                                      {" "}
                                      {posNote}
                                    </span>
                                  ) : null}
                                </span>
                              ) : null}
                            </div>
                          ) : null;
                          const col2Meta = showMeta ? (
                            <div
                              className={
                                showPosition
                                  ? "sm-print-meta-row"
                                  : "sm-print-meta-row sm-cue-meta-row-spacer"
                              }
                              aria-hidden={!showPosition}
                            >
                              {showPosition ? (
                                <span className="sm-print-position-note">
                                  Pos.
                                  {posNote ? (
                                    <span className="sm-print-position-text">
                                      {" "}
                                      {posNote}
                                    </span>
                                  ) : null}
                                </span>
                              ) : (
                                <>
                                  {showSetupNumbers ? (
                                    <span className="sm-print-setup-num">
                                      {scene.number}-{cueIndex + 1}
                                    </span>
                                  ) : null}
                                  {cameraPrint}
                                </>
                              )}
                            </div>
                          ) : null;
                          const visualIsLeft = columnOrder === "visual-audio";
                          const audioOnly = isAudioOnlyColumnOrder(columnOrder);
                          const visualTd = (
                            <td
                              key="visual"
                              className={`sm-print-visual${
                                cueShowsRowRule(cue, rowRuleMode)
                                  ? " sm-span-rule-end"
                                  : ""
                              }`}
                            >
                              {visualIsLeft ? col1Meta : col2Meta}
                              {!isCueContentEmpty(cue.visual.content) ? (
                                <Paras text={cue.visual.content} />
                              ) : null}
                            </td>
                          );
                          const audioTd = (
                            <td
                              key="audio"
                              className={
                                cueShowsRowRule(cue, rowRuleMode)
                                  ? "sm-span-rule-end"
                                  : undefined
                              }
                            >
                              {audioOnly ? (
                                showMeta ? (
                                  <div className="sm-print-meta-row">
                                    {showSetupNumbers ? (
                                      <span className="sm-print-setup-num">
                                        {scene.number}-{cueIndex + 1}
                                      </span>
                                    ) : null}
                                    {cameraPrint}
                                    {showPosition ? (
                                      <span className="sm-print-position-note">
                                        Pos.
                                        {posNote ? (
                                          <span className="sm-print-position-text">
                                            {" "}
                                            {posNote}
                                          </span>
                                        ) : null}
                                      </span>
                                    ) : null}
                                  </div>
                                ) : null
                              ) : visualIsLeft ? (
                                col2Meta
                              ) : (
                                col1Meta
                              )}
                              {!isCueContentEmpty(cue.audio.content) ? (
                                <Paras text={cue.audio.content} />
                              ) : null}
                            </td>
                          );
                          return (
                            <tr
                              key={cue.id}
                              className={
                                cueShowsRowRule(cue, rowRuleMode)
                                  ? "sm-rule-below"
                                  : undefined
                              }
                            >
                              {audioOnly ? (
                                audioTd
                              ) : columnOrder === "audio-visual" ? (
                                <>
                                  {audioTd}
                                  {visualTd}
                                </>
                              ) : (
                                <>
                                  {visualTd}
                                  {audioTd}
                                </>
                              )}
                              <td
                                className={`sm-print-source-note${
                                  cueShowsRowRule(cue, rowRuleMode)
                                    ? " sm-span-rule-end"
                                    : ""
                                }`}
                              >
                                {(() => {
                                  const text = getCueNote(
                                    cue,
                                    printNoteColumnId,
                                    primaryNoteColumnId,
                                  );
                                  return <NoteRichText text={text} />;
                                })()}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </section>
                );
              })
            : null}

          {view === "status" || view === "home" ? (
            <>
              <EpisodeStatusStats
                sceneCount={scenes.length}
                durationLabel={formatDuration(total, durFmt)}
                completed={statusRollup.completed}
                total={statusRollup.total}
                pct={statusRollup.pct}
              />
              <StatusPrintScenes
                scenes={scenes}
                statusSteps={document.production.statusSteps ?? []}
                episodeStatusSteps={episode.statusSteps ?? []}
                episodeStatusCompletedIds={episode.statusCompletedIds ?? []}
                defaultSceneHeaderBg={defaultSceneHeaderBg}
                expanded={statusPrintExpand === "expanded"}
                pageBreakBeforeScene={pageBreakBeforeScene}
                onCompactLabelsChange={handleStatusCompactLabels}
              />
            </>
          ) : null}

          {view === "script"
            ? printScenes.map((scene, sceneIndex) => {
                const cues = getSceneCues(document, scene.id);
                const audioOnly = isAudioOnlyColumnOrder(columnOrder);
                const rows = layoutRows(
                  cues,
                  document.production.castMembers ?? [],
                  withComments ? allComments : [],
                  rowRuleMode,
                  audioOnly,
                );
                const headerBg =
                  scene.headerBackground?.trim() || defaultSceneHeaderBg;
                return (
                  <section
                    key={scene.id}
                    className={`sm-print-scene${printBreakBeforeClass(pageBreakBeforeScene, sceneIndex)}`}
                  >
                    <h2
                      className="sm-print-scene-head"
                      data-scene-running={sceneRunningLabel(scene)}
                      style={{ backgroundColor: headerBg }}
                    >
                      <span className="sm-print-scene-num-line">
                        Scene {scene.number}
                        {scene.heading?.trim() ? (
                          <span className="sm-print-scene-heading">
                            {" "}
                            {scene.heading}
                          </span>
                        ) : null}
                      </span>
                      <span className="sm-print-scene-title">
                        {scene.title}
                      </span>
                      <span className="sm-print-dur">
                        {formatDuration(scene.durationSeconds, durFmt)}
                      </span>
                    </h2>
                    <table
                      className={`sm-print-table${
                        showColumnRule && !isAudioOnlyColumnOrder(columnOrder)
                          ? " sm-col-rule"
                          : ""
                      }${withComments ? " sm-print-table-comments" : ""}`}
                    >
                      <thead>
                        <tr>
                          {isAudioOnlyColumnOrder(columnOrder) ? (
                            <th scope="col">Lydside</th>
                          ) : columnOrder === "audio-visual" ? (
                            <>
                              <th scope="col">Lydside</th>
                              <th scope="col">Billedside</th>
                            </>
                          ) : (
                            <>
                              <th scope="col">Billedside</th>
                              <th scope="col">Lydside</th>
                            </>
                          )}
                          {withComments ? (
                            <th scope="col">Kommentarer</th>
                          ) : null}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row) => {
                          const rowComments = [
                            ...row.visualComments,
                            ...row.audioComments,
                          ];
                          const showCol1 = showSetupNumbers || showCameraNotes;
                          const showMeta = showCol1 || showPosition;
                          const bothCells =
                            !isAudioOnlyColumnOrder(columnOrder) &&
                            row.visual !== null &&
                            row.audio !== null;
                          const kam =
                            row.cameraNumber && row.cameraNumber >= 1
                              ? row.cameraNumber
                              : undefined;
                          const kamNote = row.cameraNote?.trim() ?? "";
                          const posNote = row.positionNote?.trim() ?? "";
                          const cameraPrint =
                            showCameraNotes && (kam != null || kamNote) ? (
                              <span className="sm-print-camera-note">
                                {kam != null ? `KAM ${kam}` : null}
                                {kamNote ? (
                                  <span className="sm-print-camera-text">
                                    {kam != null ? " " : null}
                                    {kamNote}
                                  </span>
                                ) : null}
                              </span>
                            ) : null;
                          const audioOnly = isAudioOnlyColumnOrder(columnOrder);
                          const col1Meta =
                            showMeta &&
                            (showCol1 || (showPosition && bothCells)) ? (
                              <div
                                className={
                                  showCol1
                                    ? "sm-print-meta-row"
                                    : "sm-print-meta-row sm-cue-meta-row-spacer"
                                }
                                aria-hidden={!showCol1}
                              >
                                {showSetupNumbers ? (
                                  <span className="sm-print-setup-num">
                                    {scene.number}-{row.setupIndex}
                                  </span>
                                ) : null}
                                {cameraPrint}
                                {!showCol1 ? (
                                  <span className="sm-print-position-note">
                                    Pos.
                                    {posNote ? (
                                      <span className="sm-print-position-text">
                                        {" "}
                                        {posNote}
                                      </span>
                                    ) : null}
                                  </span>
                                ) : null}
                              </div>
                            ) : null;
                          const col2Meta =
                            showMeta &&
                            (showPosition || (showCol1 && bothCells)) ? (
                              <div
                                className={
                                  showPosition
                                    ? "sm-print-meta-row"
                                    : "sm-print-meta-row sm-cue-meta-row-spacer"
                                }
                                aria-hidden={!showPosition}
                              >
                                {showPosition ? (
                                  <span className="sm-print-position-note">
                                    Pos.
                                    {posNote ? (
                                      <span className="sm-print-position-text">
                                        {" "}
                                        {posNote}
                                      </span>
                                    ) : null}
                                  </span>
                                ) : (
                                  <>
                                    {showSetupNumbers ? (
                                      <span className="sm-print-setup-num">
                                        {scene.number}-{row.setupIndex}
                                      </span>
                                    ) : null}
                                    {cameraPrint}
                                  </>
                                )}
                              </div>
                            ) : null;
                          const visualIsLeft = columnOrder === "visual-audio";
                          const visualTd =
                            row.visual !== null ? (
                              <td
                                key="visual"
                                rowSpan={row.visualSpan}
                                className={[
                                  "sm-print-visual",
                                  row.visualRuleBelow
                                    ? "sm-span-rule-end"
                                    : "",
                                ]
                                  .filter(Boolean)
                                  .join(" ")}
                                style={
                                  row.visualColor
                                    ? { color: row.visualColor }
                                    : undefined
                                }
                              >
                                {visualIsLeft ? col1Meta : col2Meta}
                                {row.visualCharacterName ? (
                                  <div className="sm-print-character">
                                    {row.visualCharacterName}
                                  </div>
                                ) : null}
                                <Paras text={row.visual} />
                              </td>
                            ) : null;
                          const audioTd =
                            row.audio !== null ? (
                              <td
                                key="audio"
                                rowSpan={row.audioSpan}
                                style={
                                  row.dialogueColor
                                    ? { color: row.dialogueColor }
                                    : undefined
                                }
                              >
                                {audioOnly ? (
                                  showMeta ? (
                                    <div className="sm-print-meta-row">
                                      {showSetupNumbers ? (
                                        <span className="sm-print-setup-num">
                                          {scene.number}-{row.setupIndex}
                                        </span>
                                      ) : null}
                                      {cameraPrint}
                                      {showPosition ? (
                                        <span className="sm-print-position-note">
                                          Pos.
                                          {posNote ? (
                                            <span className="sm-print-position-text">
                                              {" "}
                                              {posNote}
                                            </span>
                                          ) : null}
                                        </span>
                                      ) : null}
                                    </div>
                                  ) : null
                                ) : visualIsLeft ? (
                                  col2Meta
                                ) : (
                                  col1Meta
                                )}
                                {row.characterName ? (
                                  <div className="sm-print-character">
                                    {row.characterName}
                                  </div>
                                ) : null}
                                <Paras text={row.audio} />
                              </td>
                            ) : null;
                          return (
                            <tr
                              key={row.key}
                              className={
                                row.printBorderBelow
                                  ? "sm-rule-below"
                                  : undefined
                              }
                            >
                              {audioOnly ? (
                                audioTd
                              ) : visualIsLeft ? (
                                <>
                                  {visualTd}
                                  {audioTd}
                                </>
                              ) : (
                                <>
                                  {audioTd}
                                  {visualTd}
                                </>
                              )}
                              {withComments ? (
                                <td>
                                  <CommentBlock comments={rowComments} />
                                </td>
                              ) : null}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </section>
                );
              })
            : null}
        </article>
        </PaperFit>
      </div>
      <div
        className={
          pageMode === "paged"
            ? "sm-print-paged-wrap"
            : "sm-print-paged-wrap is-idle"
        }
      >
        <PaperFit widthMm={landscape ? 297 : 210} preserveLayout>
          <div ref={pagedRef} className="sm-print-paged" />
        </PaperFit>
      </div>
      </div>
    </div>
  );
}
